import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { CheckCircle as TaskIcon } from '@factorialco/f0-react/icons/app'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NodeUIAction } from 'modules/workflows/components/Node/types'
import { TaskFormSchema } from 'modules/workflows/components/Sidepanel/schemas/taskFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

import { BasicInputs } from './partials/BasicInputs'
import { SidepanelFooter } from './partials/SidepanelFooter'
import { SidepanelHeader } from './partials/SidepanelHeader'
import { SidepanelWrapper } from './partials/SidepanelWrapper'

type Props = {
  node: NodeUIAction
}

const TaskNodeSidepanel = ({ node }: Props) => {
  const { handlersConfig, setNodesUI, workflowManifest, workflow, nodesUI } = useWorkflowContext()

  const { handleSubmit, setValue, getValues, control, watch } = useFormContext<TaskFormSchema>()

  const handlerName = watch('nodeData.handlerType')
  const handlerForm = handlersConfig[handlerName]
  const HandlerFormComponent = handlerForm?.component

  useUnsafeEffect(() => {
    //When we change the handler type the form payload must be reset to the default value
    const currentHandlerForm = handlersConfig[handlerName]
    const payloadData = currentHandlerForm?.initializer?.(node.data.payload) ?? {}

    setValue('nodeData.payload', payloadData)
  }, [handlerName])

  // Get available task handler types dynamically from workflow manifest
  const taskTypes = workflowManifest.handlers
    .filter((handler) => handler.actionGroup === 'task')
    .map((type) => ({
      value: type.name,
      label: handlersConfig[type.name]?.dropDownLabel || type.name,
    }))

  // Closes form by deselecting all nodes and clearing suppressInitialValidation
  const handleCancel = () => {
    setNodesUI((prev) =>
      prev.map((n) => {
        const updated = { ...n, selected: false }
        if (n.id === node.id) {
          updated.data = { ...n.data, suppressInitialValidation: false }
        }
        return updated
      })
    )
  }

  // Updates global state and deselects all nodes
  const handleSave = () => {
    const { title, description, nodeData } = getValues()

    const defaultTitle = i18n.t('workflows.building_blocks.task.title')
    const defaultDescription = i18n.t('workflows.building_blocks.task.description')

    const normalize = (value?: string | null) => (value ?? '').trim()
    const shouldSetDefaultTitle = normalize(title) === '' || title === defaultTitle
    const shouldSetDefaultDescription =
      normalize(description) === '' || description === defaultDescription

    const extractStringField = (payload: unknown, field: 'title' | 'description') => {
      if (typeof payload !== 'object' || payload === null) {
        return undefined
      }

      const candidate = Reflect.get(payload, field)
      return typeof candidate === 'string' ? candidate : undefined
    }

    const payloadTitleRaw = extractStringField(nodeData.payload, 'title')
    const payloadDescriptionRaw = extractStringField(nodeData.payload, 'description')
    const payloadTitle = normalize(payloadTitleRaw)
    const payloadDescription = normalize(payloadDescriptionRaw)

    const resolvedTitle = shouldSetDefaultTitle
      ? payloadTitle !== ''
        ? payloadTitleRaw ?? defaultTitle
        : defaultTitle
      : title
    const resolvedDescription = shouldSetDefaultDescription
      ? payloadDescription !== ''
        ? payloadDescriptionRaw ?? defaultDescription
        : defaultDescription
      : description

    const beforeSaveData = handlerForm?.beforeSave

    setNodesUI((prev) =>
      prev.map((n) => {
        if (n.id === node.id) {
          return {
            ...node,
            data: {
              ...node.data,
              title: resolvedTitle,
              description: resolvedDescription,
              handler_name: nodeData.handlerType,
              payload: beforeSaveData
                ? beforeSaveData(nodeData.payload, nodeData.handlerType, {
                    nodeId: node.id,
                    workflow,
                    nodesUI,
                  })
                : nodeData.payload,
              // Clear suppressInitialValidation so validation kicks in after user interaction
              suppressInitialValidation: false,
            },
            selected: false,
          }
        }
        return { ...n, selected: false }
      })
    )
  }

  const onSubmit = () => {
    handleSubmit(handleSave, (_fields) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 3000,
      })
    })()
  }

  return (
    <SidepanelWrapper
      header={
        <SidepanelHeader
          nodeId={node.id}
          backgroundColor="#CBE6FF"
          icon={<TaskIcon width={22} height={22} style={{ color: '#0075E1', strokeWidth: 1.5 }} />}
          title={i18n.t('workflows.sidepanel.task.edit_title')}
          onClose={handleCancel}
        />
      }
      footer={<SidepanelFooter onCancel={handleCancel} onSave={onSubmit} isValid />}
    >
      <BasicInputs
        titleLabel={i18n.t('workflows.sidepanel.task.block_title_label')}
        titlePlaceholder={i18n.t('workflows.sidepanel.task.block_title_placeholder')}
        descriptionLabel={i18n.t('workflows.sidepanel.task.block_description_label')}
        descriptionPlaceholder={i18n.t('workflows.sidepanel.task.block_description_placeholder')}
      />

      <Box gap="s16">
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('workflows.sidepanel.task.title')}
        </Text>

        <Controller
          name="nodeData.handlerType"
          control={control}
          render={({ field, fieldState }) => (
            <F0Select
              size="md"
              label={i18n.t('workflows.sidepanel.general.required_action')}
              placeholder={i18n.t('workflows.sidepanel.general.required_action_placeholder')}
              value={field.value}
              onChange={field.onChange}
              options={taskTypes}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {HandlerFormComponent !== undefined && (
        <Box gap="s16">
          <HandlerFormComponent payload={{}} onChange={() => {}} />
        </Box>
      )}
    </SidepanelWrapper>
  )
}

export default TaskNodeSidepanel
